import { z } from 'zod';

import {
  SUPPORT_ACTOR_KIND,
  SUPPORT_ACTOR_LABEL_PREFIX,
  supportActorLabel,
} from '@order-up/models';

/**
 * Zod mirror of `SupportActor` (`packages/models/src/support/SupportActor.ts`).
 *
 * ## Why this file has to exist, and why it is not optional
 *
 * `OrderSchema` / `OrderDataSchema` / `CheckSchema` are plain `z.object()`s, and
 * a plain `z.object()` **STRIPS** every key it does not declare. Adding
 * `supportActor` to the models and stopping there would have produced a field
 * that type-checks everywhere, is written by the POS, is accepted by
 * `firestore.rules` — and is then silently deleted by the first API route that
 * validates an order on its way through. The document would keep its attribution
 * in Firestore and lose it on every server round-trip, so a check would read
 * "support wrote this" in one surface and show no actor in the next.
 *
 * That is the `removeAdditional` / `.strict()` failure class this repo has been
 * bitten by twice (see the `fastify-removeadditional-silent-strip` and
 * `employee-schema-strict-blocked-saves` notes). The schema is the second half
 * of the model, not a copy of it.
 *
 * ## Why the constants are imported, not retyped
 *
 * `kind` and the label prefix are pinned in three places — the model, this
 * schema and `firestore.rules`. Two of them can share a constant and do. The
 * third is a rules file that cannot import TypeScript, which is why
 * `check-support-actor-label-parity` reads the literal out of the rules and
 * compares it to {@link SUPPORT_ACTOR_LABEL_PREFIX}.
 */
/** `SupportActingAs` — mode plus exactly one id (the rules admit the same shape). */
export const SupportActingAsSchema = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('employee'), employeeId: z.number().int().positive() }).strict(),
  z.object({ mode: z.literal('job'), jobId: z.string().min(1) }).strict(),
]);

const SupportActorShape = z.object({
  uid: z.string().min(1),
  email: z.string().email(),
  sessionId: z.string().min(1),
  kind: z.literal(SUPPORT_ACTOR_KIND),
  /**
   * Not `z.string()` — the label is the field a human reads years later, and an
   * arbitrary string here is how it drifts. It must be the canonical
   * composition of the email beside it, which is also exactly what
   * `firestore.rules` pins, so a block that passes rules passes this and a block
   * that passes this would have passed rules.
   */
  label: z.string().startsWith(SUPPORT_ACTOR_LABEL_PREFIX),
  at: z.coerce.date(),
  /**
   * Who the operator was acting as (OrderUp Console act-as). Declared here for
   * the same reason as the whole block: an undeclared key is STRIPPED by the
   * first API round-trip, and the subject/actor split would silently become
   * "support did it, alone". Mirrors `isSupportActingAs` and the rules shape.
   */
  actingAs: SupportActingAsSchema.optional(),
});

export const SupportActorSchema = SupportActorShape.refine(
  (actor) => actor.label === supportActorLabel(actor.email),
  {
    path: ['label'],
    message:
      'supportActor.label must be the canonical label for supportActor.email — ' +
      'firestore.rules pins the same equality, so a block that fails here would ' +
      'have been refused at write time.',
  },
);

export type SupportActorType = z.infer<typeof SupportActorSchema>;
